// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import axios from 'axios';
import Dashboard  from './pages/Dashboard';
import Orders     from './pages/Orders';
import PickList   from './pages/PickList';
import Inventory  from './pages/Inventory';
import Messages   from './pages/Messages';
import SyncLogs   from './pages/SyncLogs';
import BinReport  from './pages/BinReport';
import Archive    from './pages/Archive';
import Reports    from './pages/Reports';
import Login      from './pages/Login';
import MobilePick    from './pages/MobilePick';
import ManualOrder   from './pages/ManualOrder';
import Reconciliation from './pages/Reconciliation';
import Drift from './pages/Drift';
import Help from './pages/Help';
import CompetitorResearch from './pages/CompetitorResearch';
import Labels from './pages/Labels';
import Amazon from './pages/Amazon';
import Marketplace from './pages/Marketplace';
import './App.css';

const api = axios.create({ baseURL: '/api', withCredentials: true });

const CHANNEL_NAMES = {
  ebay_source: 'Euphoric Operations',
  ebay_store2: 'TNT Operations',
  ebay_store3: 'Firestarters',
  walmart:     'Walmart',
  amazon:      'Amazon',
  mercari:     'Mercari',
  poshmark:    'Poshmark',
  depop:       'Depop',
  vinted:      'Vinted',
};
function channelLabel(ch) { return CHANNEL_NAMES[ch] || ch; }

export { api, CHANNEL_NAMES, channelLabel };

function RequireAuth({ children }) {
  const [auth, setAuth] = useState(null);
  useEffect(() => {
    api.get('/auth/me').then(r => setAuth(r.data.authenticated)).catch(() => setAuth(false));
  }, []);
  if (auth === null) return <div className="loading">Loading…</div>;
  return auth ? children : <Navigate to="/login" replace />;
}

function Nav({ unread }) {
  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode]   = useState(true);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const sections = [
    { heading: 'MAIN', links: [
      { to: '/',         icon: 'grid',     title: 'Dashboard' },
      { to: '/orders',   icon: 'package',  title: 'Orders'    },
      { to: '/picklist', icon: 'clipboard',title: 'Pick List' },
      { to: '/labels',   icon: 'package',  title: 'Labels'    },
      { to: '/inventory',icon: 'box',      title: 'Inventory' },
    ]},
    { heading: 'CHANNELS', links: [
      { to: '/messages', icon: 'mail',  title: 'Messages', badge: unread > 0 ? unread : null },
      { to: '/logs',     icon: 'refresh', title: 'Sync Logs' },
      { to: '/reconciliation', icon: 'search', title: 'Reconciliation' },
      { to: '/drift',          icon: 'refresh', title: 'Drift' },
      { to: '/amazon',         icon: 'box',     title: 'Amazon' },
      { to: '/marketplace',    icon: 'search',  title: 'Posh / Mercari' },
    ]},
    { heading: 'TOOLS', links: [
      { to: '/bins',         icon: 'archive', title: 'Bin Report' },
      { to: '/archive',      icon: 'folder',  title: 'Archive' },
      { to: '/reports',      icon: 'chart',   title: 'Reports' },
      { to: '/competitor-research', icon: 'chart', title: 'Competitor Research' },
      { to: '/manual-order', icon: 'edit',     title: 'Manual Order' },
      { to: '/help',         icon: 'book',    title: 'Help' },
    ]},
  ];

  const iconMap = {
    grid: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    package: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
    clipboard: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>,
    box: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
    mail: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    refresh: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>,
    archive: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>,
    folder: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>,
    chart: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    edit: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    search: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    book: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>,
  };

  return (
    <nav className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand" onClick={() => setCollapsed(!collapsed)}>
          <span className="brand-icon">ES</span>
          {!collapsed && <span className="brand-text">Euphoric Sync</span>}
        </div>
      </div>
      <div className="sidebar-sections">
        {sections.map(sec => (
          <div key={sec.heading} className="sidebar-section">
            {!collapsed && <div className="sidebar-heading">{sec.heading}</div>}
            {sec.links.map(l => (
              <NavLink key={l.to} to={l.to} end={l.to === '/'}
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                title={l.title}>
                <span className="sidebar-icon">{iconMap[l.icon]}</span>
                {!collapsed && <span className="sidebar-label">{l.title}</span>}
                {!collapsed && l.badge && <span className="sidebar-badge">{l.badge}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
      <div className="sidebar-footer">
        <button className="sidebar-link theme-toggle" onClick={() => setDarkMode(!darkMode)}>
          <span className="sidebar-icon">
            {darkMode
              ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
            }
          </span>
          {!collapsed && <span className="sidebar-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>
      </div>
    </nav>
  );
}

export default function App() {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const poll = () => {
      api.get('/messages?limit=1').then(r => {
        const count = r.data.unreadCounts?.reduce((a, b) => a + b.count, 0) || 0;
        setUnread(count);
      }).catch(() => {});
    };
    poll();
    const t = setInterval(poll, 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/pick" element={<MobilePick />} />
        <Route path="/*" element={
          <RequireAuth>
            <div className="app-shell sidebar-layout">
              <Nav unread={unread} />
              <main className="main-content">
                <Routes>
                  <Route path="/"          element={<Dashboard />} />
                  <Route path="/orders"    element={<Orders />} />
                  <Route path="/picklist"  element={<PickList />} />
                  <Route path="/labels"    element={<Labels />} />
                  <Route path="/inventory" element={<Inventory />} />
                  <Route path="/messages"  element={<Messages setUnread={setUnread} />} />
                  <Route path="/logs"      element={<SyncLogs />} />
                  <Route path="/bins"      element={<BinReport />} />
                  <Route path="/archive"   element={<Archive />} />
                  <Route path="/reports"       element={<Reports />} />
                  <Route path="/manual-order" element={<ManualOrder />} />
                  <Route path="/reconciliation" element={<Reconciliation />} />
                  <Route path="/drift"          element={<Drift />} />
                  <Route path="/amazon"         element={<Amazon />} />
                  <Route path="/marketplace"    element={<Marketplace />} />
                  <Route path="/help"           element={<Help />} />
                  <Route path="/help/:slug"     element={<Help />} />
                  <Route path="/competitor-research" element={<CompetitorResearch />} />
                </Routes>
              </main>
            </div>
          </RequireAuth>
        } />
      </Routes>
    </BrowserRouter>
  );
}
